import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { InvoiceService } from '../invoice.service';
import { Invoice } from '../models/invoice.model';
import { FormControl, FormBuilder, FormGroup } from '@angular/forms';
import { catchError, concatMap, finalize, from, map, Observable, of, tap } from 'rxjs';
import { NotificationService } from '../../../shared/services/notification.service';

export interface EmailStatus {
  state: 'sending' | 'sent' | 'failed';
  error?: string;
}

@Component({
  selector: 'app-invoice-generator',
  standalone: false,
  templateUrl: './invoice-generator.component.html',
  styleUrl: './invoice-generator.component.scss'
})
export class InvoiceGeneratorComponent implements OnInit {

  invoices: Invoice[] = [];
  invoiceDate = new FormControl();
  mode: 'preview' | 'generate' | 'complete' = 'preview';
  showConfirmDialog = false;
  showEmailConfirmDialog = false;
  batchSending = false;
  emailStatus: Partial<Record<number, EmailStatus>> = {};

  constructor(private invoiceService: InvoiceService, private notificationService: NotificationService) { }

  ngOnInit(): void {
    const today = new Date();
    const iso = today.toISOString().substring(0,10);
    this.invoiceDate.setValue(iso);
  }

  previewInvoices() {
    if (!this.invoiceDate.value) {
      return;
    }
    const date = new Date(this.invoiceDate.value);
    this.invoiceService.previewInvoices(date).subscribe((response) => {
      this.invoices = response;
      this.mode = 'generate';
    });
  }

  confirmGenerate() {
    this.showConfirmDialog = true;
  }

  cancelGenerate() {
    this.showConfirmDialog = false;
  }

  generateInvoices() {
    if (!this.invoiceDate.value) {
      return;
    }
    const date = new Date(this.invoiceDate.value);
    this.invoiceService.generateInvoices(date).subscribe((response) => {
      this.showConfirmDialog = false;
      this.invoices = response;
      this.mode = 'complete';
    });
  }

  confirmEmailAll() {
    this.showEmailConfirmDialog = true;
  }

  cancelEmailAll() {
    this.showEmailConfirmDialog = false;
  }

  // Sends one at a time so each row's status updates as it goes; failures are recorded and skipped.
  emailAll() {
    this.showEmailConfirmDialog = false;
    this.batchSending = true;
    let sent = 0;
    let failed = 0;
    from(this.invoices).pipe(
      concatMap((invoice) => this.sendEmail(invoice)),
      finalize(() => this.batchSending = false)
    ).subscribe({
      next: (success) => success ? sent++ : failed++,
      complete: () => {
        if (failed === 0) {
          this.notificationService.success(`Emailed all ${sent} invoices.`);
        } else {
          this.notificationService.warning(`Emailed ${sent} of ${sent + failed} invoices. ${failed} failed - see the highlighted rows.`);
        }
      }
    });
  }

  emailOne(invoice: Invoice) {
    this.sendEmail(invoice).subscribe((success) => {
      if (success) {
        this.notificationService.success(`Invoice ${invoice.id} emailed to ${invoice.fullName}`);
      } else {
        this.notificationService.error(this.emailStatus[invoice.id]?.error ?? `Failed to email invoice ${invoice.id}`);
      }
    });
  }

  isSending(invoice: Invoice): boolean {
    return this.emailStatus[invoice.id]?.state === 'sending';
  }

  hasFailed(invoice: Invoice): boolean {
    return this.emailStatus[invoice.id]?.state === 'failed';
  }

  private sendEmail(invoice: Invoice): Observable<boolean> {
    this.emailStatus[invoice.id] = { state: 'sending' };
    return this.invoiceService.emailInvoice(invoice.id).pipe(
      tap((updated) => {
        invoice.emailedOn = updated.emailedOn;
        this.emailStatus[invoice.id] = { state: 'sent' };
      }),
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        this.emailStatus[invoice.id] = {
          state: 'failed',
          error: error.error?.message ?? `Failed to email invoice ${invoice.id}`
        };
        return of(false);
      })
    );
  }

  reset() {
    this.invoices = [];
    this.mode = 'preview';
    this.showConfirmDialog = false;
    this.showEmailConfirmDialog = false;
    this.emailStatus = {};
    const today = new Date();
    const iso = today.toISOString().substring(0, 10);
    this.invoiceDate.setValue(iso);
  }

  get confirmationText(): string {
    return `This will generate invoices for ${this.invoices.length} members. Would you like to continue?`;
  }

  get emailConfirmationText(): string {
    const alreadySent = this.invoices.filter((invoice) => invoice.emailedOn).length;
    const resendText = alreadySent > 0 ? ` ${alreadySent} of them have already been emailed and will be sent again.` : '';
    return `This will email ${this.invoices.length} invoices to members.${resendText} Would you like to continue?`;
  }

}
